import React, { useEffect, useState } from 'react'
import AgTechHeaderDetail, { AgTechHeaderDetailProps } from '../../Headers/AgTechHeaderDetail';
import AgTechCloseIcon from '../../Icons/AgTechCloseIcon';
import { AgTechQueryBuilder, AgTechQueryRun } from '@agtech/core/data/queries/AgTechDataQueries';
import AgTechQueryLoadingSurface from '@agtech/core/data/queries/AgTechQueryLoadingSurface';
import { ErrorBoundary } from 'react-error-boundary';
import { useDialogContext } from '@agtech/core/forms/dialogs/AgTechDialogs';
import { isDev } from '@agtech/core/utilities/AgTechUtilities';

export type AgTechViewDialogProps<TQueryResponseData, TDialogData = TQueryResponseData> = {
    data?: {
        query: AgTechQueryRun<TQueryResponseData>,
        projection?: (data: TQueryResponseData) => TDialogData
    },
    structure: {
        header?: (data: TDialogData) => AgTechHeaderDetailProps,
        body?: {
            classes?: string
        },
        footer?: (data: TDialogData, closeDialog: (e: React.MouseEvent<HTMLElement>) => void) => React.ReactNode,
        width: string,
    }
    settings?: {
        growAfterLoading?: boolean
    },
    children: (
        data: TDialogData,
        refresh: () => Promise<any>,
        closeDialog: (e: React.MouseEvent<HTMLElement>) => void
    ) => JSX.Element
}

const AgTechViewDialog = <TQueryResponseData, TDialogData = TQueryResponseData>(
    props: AgTechViewDialogProps<TQueryResponseData, TDialogData>
) => {
    const [hasRendered, setHasRendered] = useState(false);
    const [hasGrown, setHasGrown] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);

    const shouldGrow = props.settings?.growAfterLoading ?? true;
    const dialogWidth = ((hasRendered && hasLoaded) || !shouldGrow) ? props.structure.width : '400px';

    const dialogContext = useDialogContext();

    const handleDialogTransitionEnd = (e: React.TransitionEvent) => {
        if (e.propertyName === 'width') {
            setHasGrown(true);
        }
    }

    setTimeout(() => {
        setHasRendered(true);
    }, 500);

    useEffect(() => {
        if (!shouldGrow) {
            setHasGrown(true);
        }
    }, []);

    const dialogData = props.data ?? {
        query: AgTechQueryBuilder.buildQueryResult({} as any)
    };

    return (
        <div
            className={`agtech-dialog agtech-view-dialog ${hasGrown ? 'loaded' : ''}`}
            style={{ width: dialogWidth }}
            onClick={e => e.stopPropagation()}
            onTransitionEnd={handleDialogTransitionEnd}
        >
            <AgTechQueryLoadingSurface
                {...dialogData}
                hasFinishedLoading={hasRendered}
                errorBoundary={AgTechViewDialogErrorBoundary}
            >
                {(data, refresh) => {
                    setHasLoaded(true);

                    const headerConfiguration = props.structure.header?.(data);

                    return (
                        <>
                            {hasGrown && (
                                <div className="row-based non-scrollable stretched fading-in flex">
                                    {headerConfiguration ? (
                                        <div className="row-based bottom-bordered py-3 px-5 min-h-[48px] flex">
                                            <AgTechHeaderDetail
                                                {...headerConfiguration}
                                                settings={{
                                                    header: {
                                                        classes: '!text-plus'
                                                    },
                                                    detail: {
                                                        classes: 'mt-2 whitespace-break-spaces'
                                                    },
                                                    content: {
                                                        body: () => (
                                                            <div className='ml-auto tall column-based flex'>
                                                                {headerConfiguration.settings?.content?.body?.()}
                                                                {headerConfiguration.settings?.content?.body ? (
                                                                    <div className='tall centered pl-4 ml-4 border-l flex'>
                                                                        <AgTechCloseIcon
                                                                            onClicked={async (e) => dialogContext.closeDialog(e)} 
                                                                            classes="ml-auto z-10"
                                                                            size={32}
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <AgTechCloseIcon
                                                                        onClicked={async (e) => dialogContext.closeDialog(e)} 
                                                                        classes="ml-auto z-10"
                                                                        size={32}
                                                                    />
                                                                )}
                                                            </div>
                                                        )}
                                                    }
                                                }
                                            />
                                        </div>
                                    ) : (
                                        <AgTechCloseIcon
                                            onClicked={async (e) => dialogContext.closeDialog(e)} 
                                            classes="absolute top-[20px] right-[20px] z-10"
                                            size={32}
                                        />
                                    )}
                                    <div className="flexed relative non-scrollable flex">
                                        <div className={`stretched scrollable bg-surface flex ${props.structure.body?.classes ?? ''}`}>
                                            {props.children(data, refresh, (e) => dialogContext.closeDialog(e))}
                                        </div>
                                    </div>
                                    { props.structure.footer ? (
                                        <div className="agtech-dialog-footer">
                                            {props.structure.footer(data, e => dialogContext.closeDialog(e))}
                                        </div>
                                    ) : null}
                                </div>
                            )}
                        </>
                    )
                }}
            </AgTechQueryLoadingSurface>
        </div>
    )
};

const AgTechViewDialogErrorBoundary = (props: {
    children: React.ReactNode
}) => {
    const dialogContext = useDialogContext();

    return (
        <ErrorBoundary
            fallbackRender={(props) => {
                const errorDetails = {
                    message: props.error.message,
                    callStack: props.error.stack,
                };

                return (
                    <div className='stretched centered relative row-based flex'>
                        <AgTechCloseIcon
                            onClicked={async (e) => dialogContext.closeDialog(e)} 
                            classes="absolute top-[20px] right-[16px] z-10"
                            size={32}
                        />
                        <i className={`fas fa-exclamation text-md w-[32px] h-[32px] bg-red font-color-white mb-4 centered rounded-[50%] flex`} />
                        <h1 className='text-xl font-semibold mb-2' >An error occurred.</h1>
                        <h3 className='text-plus text-detail'>Please close and open this dialog again.</h3>
                        {isDev() && (
                            <textarea className="mt-4 w-[60%] min-h-[240px]">
                                {JSON.stringify(errorDetails, null, 4)}
                            </textarea>
                        )}
                    </div>
                )
            }}
        >
            {props.children}
        </ErrorBoundary>
    )
}

export default AgTechViewDialog;